import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import getHome from '../utils/getHome';

import Ikon from '../components/Ikon';
import { BRAND } from '../utils/brand';

export default function LoginPage() {
  const { login, logout, loading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: '', password: '' });
  const [rememberMe, setRememberMe] = useState(() => {
    return localStorage.getItem('ft_remember') === '1';
  });
  const [error, setError] = useState('');
  const [showPass, setShowPass] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('logout') === '1' || params.get('force') === '1') {
      localStorage.removeItem('ft_token');
      localStorage.removeItem('ft_user');
      sessionStorage.removeItem('ft_session_token');
      sessionStorage.removeItem('ft_session_user');
      logout?.();
    } else if (user) {
      navigate(getHome(user), { replace: true });
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // Simpan preferensi remember me
    if (rememberMe) localStorage.setItem('ft_remember', '1');
    else localStorage.removeItem('ft_remember');

    // Kasir: email POS → POS, Email Portal → Portal Karyawan. Server yang
    // memutuskan dari email mana yang cocok (routes/auth.js), bukan halaman ini.
    const res = await login(form.email, form.password, rememberMe);
    if (res?.success) {
      navigate(getHome(res.user));
    } else {
      setError(res?.message || 'Login gagal');
    }
  };

  return (
    <div className="relative flex justify-center min-h-screen overflow-hidden bg-gradient-brand">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute rounded-full -top-40 -right-40 w-96 h-96 bg-white/10 blur-3xl" />
        <div className="absolute rounded-full -bottom-40 -left-40 w-96 h-96 bg-white/10 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl" />
        {[...Array(6)].map((_, i) => (
          <div key={i} className="absolute w-2 h-2 rounded-full bg-white/20 animate-pulse" style={{ top: `${10 + i * 15}%`, left: `${5 + i * 8}%`, animationDelay: `${i * 0.5}s` }} />
        ))}
      </div>

      <div className="relative z-10 flex flex-col justify-center flex-1 p-6 lg:max-w-lg lg:p-12">
        <div className="p-8 bg-white shadow-2xl rounded-3xl lg:p-10 animate-slide-up">
          <div className="flex items-center gap-3 mb-8">
            <div className="flex items-center justify-center w-10 h-10 overflow-hidden shadow-md rounded-xl">
              <img src={BRAND.logo} alt={BRAND.nama} className="object-cover w-full h-full" />
            </div>
            <div>
              <h1 className="text-xl font-bold font-display text-brand-700">{BRAND.nama}</h1>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 px-4 py-3 mb-5 text-sm text-red-700 border border-red-200 bg-red-50 rounded-xl">
              <span><Ikon e="⚠" /></span> {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block mb-2 text-sm font-semibold text-slate-700">Email</label>
              <input type="email" required className="pl-10 input-field" placeholder="email@fasttrack.id" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>

            <div>
              <label className="block mb-2 text-sm font-semibold text-slate-700">Password</label>
              <div className="relative">
                <input type={showPass ? 'text' : 'password'} required className="pl-10 pr-12 input-field" placeholder="••••••••" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3.5 top-2 text-slate-400 hover:text-slate-600 text-sm p-1">
                  {showPass ? (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-5">
                      <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                      <path
                        fillRule="evenodd"
                        d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 0 1 0-1.113ZM17.25 12a5.25 5.25 0 1 1-10.5 0 5.25 5.25 0 0 1 10.5 0Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-5">
                      <path d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-18-18ZM22.676 12.553a11.249 11.249 0 0 1-2.631 4.31l-3.099-3.099a5.25 5.25 0 0 0-6.71-6.71L7.759 4.577a11.217 11.217 0 0 1 4.242-.827c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113Z" />
                      <path d="M15.75 12c0 .18-.013.357-.037.53l-4.244-4.243A3.75 3.75 0 0 1 15.75 12ZM12.53 15.713l-4.243-4.244a3.75 3.75 0 0 0 4.244 4.243Z" />
                      <path d="M6.75 12c0-.619.107-1.213.304-1.764l-3.1-3.1a11.25 11.25 0 0 0-2.63 4.31c-.12.362-.12.752 0 1.114 1.489 4.467 5.704 7.69 10.675 7.69 1.5 0 2.933-.294 4.242-.827l-2.477-2.477A5.25 5.25 0 0 1 6.75 12Z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center gap-2.5 group cursor-pointer" onClick={() => setRememberMe(!rememberMe)}>
              <div className="relative flex items-center">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-5 h-5 transition-all border-2 rounded appearance-none cursor-pointer border-slate-300 checked:bg-brand-600 checked:border-brand-600 focus:ring-2 focus:ring-brand-500/20"
                />
                {rememberMe && (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" className="absolute w-3.5 h-3.5 text-white left-0.5 pointer-events-none">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
              <span className="text-sm font-medium transition-colors select-none text-slate-600 group-hover:text-brand-700">Ingat Saya</span>
            </div>

            <button type="submit" disabled={loading} className="flex items-center justify-center w-full gap-2 py-3 text-base btn-primary disabled:opacity-60 disabled:cursor-not-allowed">
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 rounded-full border-white/30 border-t-white animate-spin" /> Masuk...
                </>
              ) : (
                'Masuk →'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
